import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Chip, Tooltip, Grid, Card, CardContent, TextField, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Delete, Visibility, Timeline, Search, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getAPIUsageLogs, getAPIUsageSummary, clearAPIUsageLogs } from '../../services/apiManagement';
import { useDebounce } from '../../hooks/useDebounce';

const APIUsageLogs = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [providerSiteFilter, setProviderSiteFilter] = useState('all');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Calculate total pages and total count
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchLogs = useCallback(async (
    pageArg,
    rowsArg,
    search = '',
    status = 'all',
    method = 'all',
    provider = 'all',
    providerSite = 'all',
    endpoint = '',
    user = '',
    from = '',
    to = ''
  ) => {
    setLoading(true);
    try {
      const params = {
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        search,
        status: status === 'all' ? '' : status,
        method: method === 'all' ? '' : method,
        provider: provider === 'all' ? '' : provider,
        provider_site: providerSite,
        endpoint_name: endpoint,
        user,
        date_from: from,
        date_to: to
      };
      const [logsResponse, summaryResponse] = await Promise.all([
        getAPIUsageLogs(params),
        getAPIUsageSummary({
          provider_site: providerSite,
          endpoint_name: endpoint,
          user,
          date_from: from,
          date_to: to,
        })
      ]);
      
      if (logsResponse.data) {
        setLogs(logsResponse.data.results || []);
        setTotalPages(logsResponse.data.total_pages || 1);
        setTotalCount(logsResponse.data.count || 0);
      }
      setSummary(summaryResponse.data || null);
    } catch (error) {
      console.error('Error fetching API usage logs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch API usage logs',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-apply filters when debounced values change
  useEffect(() => {
    setPage(0); // Reset to first page when filters change
    fetchLogs(0, rowsPerPage, debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo);
  }, [debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo, rowsPerPage, fetchLogs]);

  // Handle pagination changes (only when page > 0 to avoid double fetch)
  useEffect(() => {
    if (page > 0) {
      fetchLogs(page, rowsPerPage, debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo);
    }
  }, [page, fetchLogs, rowsPerPage, debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo]);

  // Refresh function with current filters
  const handleRefresh = () => {
    setPage(0);
    fetchLogs(0, rowsPerPage, debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo);
  };

  // Search and filter handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleMethodFilterChange = (event) => {
    setMethodFilter(event.target.value);
  };

  const handleProviderFilterChange = (event) => {
    setProviderFilter(event.target.value);
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleClearLogs = async () => {
    try {
      await clearAPIUsageLogs();
      setSnackbar({
        open: true,
        message: 'All API usage logs cleared successfully',
        severity: 'success'
      });
      setShowClearDialog(false);
      fetchLogs(0, rowsPerPage, debouncedSearchTerm, statusFilter, methodFilter, providerFilter, providerSiteFilter, endpointFilter, userFilter, dateFrom, dateTo);
    } catch (error) {
      console.error('Error clearing logs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to clear logs',
        severity: 'error'
      });
    }
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setShowLogDialog(true);
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'primary';
      case 'POST': return 'success';
      case 'PUT': return 'warning';
      case 'DELETE': return 'error';
      case 'PATCH': return 'info';
      default: return 'default';
    }
  };

  const formatResponseTime = (time) => {
    if (!time) return 'N/A';
    return `${(time * 1000).toFixed(2)}ms`;
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString();

  const formatUser = (user) => {
    if (!user) return 'Unattributed';
    return user.fullname || user.username || user.email || user.phone || `User #${user.id}`;
  };

  const formatSummaryUser = (row) => (
    row.user__fullname || row.user__username || row.user__email || row.user__phone || (row.user_id ? `User #${row.user_id}` : 'Unattributed')
  );

  const providerSiteLabel = providerSiteFilter === 'all' ? 'All Providers' : providerSiteFilter.replace(/_/g, ' ');

  // Add serial numbers to logs for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: 'S/N', width: 80, sortable: false, filterable: false },
    {
      field: 'endpoint_name',
      headerName: 'Provider Endpoint',
      width: 150,
      renderCell: (params) => (
        <Chip label={params.value || 'N/A'} size="small" variant="outlined" />
      )
    },
    {
      field: 'user',
      headerName: 'User',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatUser(params.value)}
        </Typography>
      )
    },
    {
      field: 'provider_name',
      headerName: 'Provider',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'translation_provider',
      headerName: 'Translation Provider',
      width: 160,
      renderCell: (params) => (
        params.value ? <Chip label={params.value} size="small" color="info" variant="outlined" /> : 'N/A'
      )
    },
    {
      field: 'cache_hit',
      headerName: 'Cache',
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value ? 'Hit' : 'No'} size="small" color={params.value ? 'success' : 'default'} />
      )
    },
    {
      field: 'api_key_name',
      headerName: 'API Key',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'endpoint',
      headerName: 'Endpoint',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getMethodColor(params.value)}
        />
      )
    },
    {
      field: 'response_status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color={getStatusColor(params.value)}
        />
      )
    },
    {
      field: 'response_time',
      headerName: 'Response Time',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {formatResponseTime(params.value)}
        </Typography>
      )
    },
    {
      field: 'source_character_count',
      headerName: 'Source Chars',
      width: 120,
      renderCell: (params) => formatNumber(params.value)
    },
    {
      field: 'translated_character_count',
      headerName: 'Translated Chars',
      width: 140,
      renderCell: (params) => formatNumber(params.value)
    },
    {
      field: 'billed_character_count',
      headerName: 'Billed Chars',
      width: 120,
      renderCell: (params) => formatNumber(params.value)
    },
    {
      field: 'created_at',
      headerName: 'Timestamp',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {new Date(params.value).toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewLog(params.row)}
              color="primary"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const dailyEndpointColumns = [
    { field: 'day', headerName: 'Date', width: 130 },
    { field: 'endpoint_name', headerName: 'Endpoint', flex: 1, minWidth: 160 },
    { field: 'total_hits', headerName: 'Real Hits', width: 110 },
    { field: 'failed_hits', headerName: 'Failed', width: 100 },
    { field: 'skipped_hits', headerName: 'Skipped', width: 100 },
    { field: 'cache_hits', headerName: 'Cache', width: 100 },
    { field: 'logged_hits', headerName: 'Logged', width: 100 },
  ];

  const userEndpointColumns = [
    { field: 'day', headerName: 'Date', width: 130 },
    { field: 'user_label', headerName: 'User', flex: 1, minWidth: 180 },
    { field: 'endpoint_name', headerName: 'Endpoint', width: 160 },
    { field: 'total_hits', headerName: 'Real Hits', width: 110 },
    { field: 'failed_hits', headerName: 'Failed', width: 100 },
    { field: 'skipped_hits', headerName: 'Skipped', width: 100 },
    { field: 'cache_hits', headerName: 'Cache', width: 100 },
    { field: 'logged_hits', headerName: 'Logged', width: 100 },
  ];

  const dailyEndpointRows = (summary?.daily_endpoint_totals || []).map((row, index) => ({
    id: `${row.day || 'unknown'}-${row.endpoint_name || 'unknown'}-${index}`,
    ...row,
  }));

  const userEndpointRows = (summary?.user_endpoint_totals || []).map((row, index) => ({
    id: `${row.day || 'unknown'}-${row.user_id || 'none'}-${row.endpoint_name || 'unknown'}-${index}`,
    ...row,
    user_label: formatSummaryUser(row),
  }));

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            API Usage Logs
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Monitor and track all API calls and their performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setShowClearDialog(true)}
          >
            Clear All Logs
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Search logs..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                size="small"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="200-299">Success (200-299)</MenuItem>
                <MenuItem value="400-499">Client Error (400-499)</MenuItem>
                <MenuItem value="500-599">Server Error (500-599)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Method"
                value={methodFilter}
                onChange={handleMethodFilterChange}
                size="small"
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Provider"
                value={providerFilter}
                onChange={handleProviderFilterChange}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Source"
                value={providerSiteFilter}
                onChange={(event) => setProviderSiteFilter(event.target.value)}
                size="small"
              >
                <MenuItem value="1688">1688</MenuItem>
                <MenuItem value="google_translate">Google Translation</MenuItem>
                <MenuItem value="azure_translate">Azure Translation</MenuItem>
                <MenuItem value="baidu_translate">Baidu Translation</MenuItem>
                <MenuItem value="google_public_translate">Google Public Translation</MenuItem>
                <MenuItem value="cache_translate">Translation Cache</MenuItem>
                <MenuItem value="all">All Providers</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Endpoint"
                value={endpointFilter}
                onChange={(event) => setEndpointFilter(event.target.value)}
                size="small"
              >
                <MenuItem value="">All Endpoints</MenuItem>
                <MenuItem value="item_search">item_search</MenuItem>
                <MenuItem value="item_get">item_get</MenuItem>
                <MenuItem value="item_get_app">item_get_app</MenuItem>
                <MenuItem value="item_fee">item_fee</MenuItem>
                <MenuItem value="item_search_img">item_search_img</MenuItem>
                <MenuItem value="item_review">item_review</MenuItem>
                <MenuItem value="upload_img">upload_img</MenuItem>
                <MenuItem value="google_translate_text">google_translate_text</MenuItem>
                <MenuItem value="google_translate_text_client">google_translate_text_client</MenuItem>
                <MenuItem value="azure_translate_text">azure_translate_text</MenuItem>
                <MenuItem value="baidu_translate_text">baidu_translate_text</MenuItem>
                <MenuItem value="google_public_translate_text">google_public_translate_text</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="User"
                placeholder="Username, email, phone"
                value={userFilter}
                onChange={(event) => setUserFilter(event.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="From"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="To"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {summary?.totals?.total_hits ?? totalCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {providerSiteLabel} Real API Hits
                  </Typography>
                </Box>
                <Timeline color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {summary?.totals?.successful_hits || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Successful
                  </Typography>
                </Box>
                <Chip label="200-299" color="success" size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {summary?.totals?.failed_hits || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed
                  </Typography>
                </Box>
                <Chip label="400+" color="error" size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {summary?.totals?.unique_users || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Unique Users
                  </Typography>
                </Box>
                <Timeline color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 1688 Aggregate Tables */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Daily Provider Endpoint Totals
              </Typography>
              <Box sx={{ height: 360, width: '100%' }}>
                <DataGrid
                  rows={dailyEndpointRows}
                  columns={dailyEndpointColumns}
                  hideFooter
                  disableSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeader': { backgroundColor: theme => theme.palette.primary.main },
                    '& .MuiDataGrid-columnHeaderTitle': { color: 'white', fontWeight: 'bold' },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: 'white' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                User Provider Endpoint Totals
              </Typography>
              <Box sx={{ height: 360, width: '100%' }}>
                <DataGrid
                  rows={userEndpointRows}
                  columns={userEndpointColumns}
                  hideFooter
                  disableSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeader': { backgroundColor: theme => theme.palette.primary.main },
                    '& .MuiDataGrid-columnHeaderTitle': { color: 'white', fontWeight: 'bold' },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: 'white' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Usage Logs Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {logs.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  No API usage logs found
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  Refresh Data
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(logs, page, rowsPerPage)}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1900, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Log Details Dialog */}
      <Dialog open={showLogDialog} onClose={() => setShowLogDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>API Call Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Provider</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.provider_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">API Key</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.api_key_name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">User</Typography>
                  <Typography variant="body1" gutterBottom>{formatUser(selectedLog.user)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Provider Endpoint</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.endpoint_name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Translation Provider</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.translation_provider || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Cache Hit</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.cache_hit ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Method</Typography>
                  <Chip label={selectedLog.method} color={getMethodColor(selectedLog.method)} size="small" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip label={selectedLog.response_status || 'N/A'} color={getStatusColor(selectedLog.response_status)} size="small" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="textSecondary">Endpoint</Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }} gutterBottom>
                    {selectedLog.endpoint}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Response Time</Typography>
                  <Typography variant="body1" gutterBottom>{formatResponseTime(selectedLog.response_time)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedLog.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Source Characters</Typography>
                  <Typography variant="body1" gutterBottom>{formatNumber(selectedLog.source_character_count || selectedLog.request_data?.char_count)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Translated Characters</Typography>
                  <Typography variant="body1" gutterBottom>{formatNumber(selectedLog.translated_character_count || selectedLog.response_data?.translated_char_count)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Billed Characters</Typography>
                  <Typography variant="body1" gutterBottom>{formatNumber(selectedLog.billed_character_count)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Origin</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.metadata?.origin || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Error Category</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLog.metadata?.error_category || 'N/A'}</Typography>
                </Grid>
                {selectedLog.error_message && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="textSecondary">Error Message</Typography>
                    <Typography variant="body1" color="error" sx={{ wordBreak: 'break-all' }}>
                      {selectedLog.error_message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear Logs Confirmation Dialog */}
      <Dialog open={showClearDialog} onClose={() => setShowClearDialog(false)}>
        <DialogTitle>Clear All Logs</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all API usage logs? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearDialog(false)}>Cancel</Button>
          <Button onClick={handleClearLogs} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default APIUsageLogs;
